/**
 * SA 「請求可能」タブ (v3.4 で 2 セクション構成に刷新)。
 *
 *  - 商品報酬: 自分の sales_records (status='pending', recipientType='sa')
 *  - 個別説明代行料: 未請求の briefing_records (listMyUnbilledBriefings Callable)
 *
 * 両セクションからチェックで選択 (既定: 全選択) → 発行前プレビューダイアログ →
 * commission_invoice を生成 + 経理にメール。報酬 0 件でも個別説明のみで発行可能。
 * チェックを外した個別説明は未請求のまま次回に持ち越される。
 */
import { CheckSquare, Coins, FileText, Presentation, Square } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { useMyUnbilledBriefings } from '@/hooks/useCommissionInvoices';
import { useMyCommissionRecords } from '@/hooks/useCommissionRecords';

import { SaInvoiceIssueDialog } from './SaInvoiceIssueDialog';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

const briefingKey = (b: { dealId: string; recordId: string }) => `${b.dealId}/${b.recordId}`;

interface Props {
  /** 発行成功後にタブを「発行済請求書」へ切り替える */
  onIssued?: () => void;
}

export function SaInvoiceableTab({ onIssued }: Props) {
  const { user } = useAuth();
  const records = useMyCommissionRecords(user?.uid);
  const unbilled = useMyUnbilledBriefings(user?.uid);

  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [selectedBriefings, setSelectedBriefings] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  // SA タイプの pending のみ表示
  const pendingRecords = useMemo(() => {
    return (records.data ?? []).filter(
      (r) => r.recipientType === 'sa' && r.status === 'pending',
    );
  }, [records.data]);

  const briefingFee = unbilled.data?.briefingFee ?? 0;
  const briefingItems = useMemo(
    () => (briefingFee > 0 ? unbilled.data?.items ?? [] : []),
    [briefingFee, unbilled.data],
  );

  // 初回ロード時は「全選択」を既定にする (データ再取得時は選択を有効な範囲に絞り直す)
  const recordsInitialized = useRef(false);
  useEffect(() => {
    if (!records.data) return;
    const validPaths = new Set(pendingRecords.map((r) => r.path));
    if (!recordsInitialized.current) {
      recordsInitialized.current = true;
      setSelectedRecords(validPaths);
    } else {
      setSelectedRecords((prev) => new Set([...prev].filter((p) => validPaths.has(p))));
    }
  }, [records.data, pendingRecords]);

  const briefingsInitialized = useRef(false);
  useEffect(() => {
    if (!unbilled.data) return;
    const validKeys = new Set(briefingItems.map(briefingKey));
    if (!briefingsInitialized.current) {
      briefingsInitialized.current = true;
      setSelectedBriefings(validKeys);
    } else {
      setSelectedBriefings((prev) => new Set([...prev].filter((k) => validKeys.has(k))));
    }
  }, [unbilled.data, briefingItems]);

  const selectedRecordObjs = useMemo(
    () => pendingRecords.filter((r) => selectedRecords.has(r.path)),
    [pendingRecords, selectedRecords],
  );
  const selectedBriefingObjs = useMemo(
    () => briefingItems.filter((b) => selectedBriefings.has(briefingKey(b))),
    [briefingItems, selectedBriefings],
  );

  const recordTotal = selectedRecordObjs.reduce((s, r) => s + r.finalAmount, 0);
  const briefingTotal = selectedBriefingObjs.length * briefingFee;
  const grandTotal = recordTotal + briefingTotal;
  const pendingRecordTotal = pendingRecords.reduce((s, r) => s + r.finalAmount, 0);

  const toggleRecord = (path: string) => {
    setSelectedRecords((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };
  const toggleAllRecords = () => {
    setSelectedRecords((prev) =>
      prev.size === pendingRecords.length
        ? new Set()
        : new Set(pendingRecords.map((r) => r.path)),
    );
  };
  const toggleBriefing = (key: string) => {
    setSelectedBriefings((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAllBriefings = () => {
    setSelectedBriefings((prev) =>
      prev.size === briefingItems.length
        ? new Set()
        : new Set(briefingItems.map(briefingKey)),
    );
  };

  if (records.isLoading || unbilled.isLoading) {
    return <Spinner />;
  }
  if (pendingRecords.length === 0 && briefingItems.length === 0) {
    return (
      <EmptyState
        icon={Coins}
        title="請求可能なレコードがありません"
        description="入金確定後の SA タイプの報酬レコード、および未請求の個別説明代行料がここに表示されます。"
      />
    );
  }

  const allRecordsSelected =
    pendingRecords.length > 0 && selectedRecords.size === pendingRecords.length;
  const allBriefingsSelected =
    briefingItems.length > 0 && selectedBriefings.size === briefingItems.length;
  const nothingSelected = selectedRecordObjs.length === 0 && selectedBriefingObjs.length === 0;

  return (
    <div className="space-y-3">
      {/* サマリー */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
          <div className="text-[10px] text-[var(--text-muted)]">請求可能な報酬</div>
          <div className="text-lg font-semibold tabular-nums">
            ¥{pendingRecordTotal.toLocaleString()}
            <span className="ml-1 text-[10px] font-normal text-[var(--text-muted)]">
              / {pendingRecords.length} 件
            </span>
          </div>
        </div>
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
          <div className="text-[10px] text-[var(--text-muted)]">未請求の個別説明</div>
          <div className="text-lg font-semibold tabular-nums">
            ¥{(briefingItems.length * briefingFee).toLocaleString()}
            <span className="ml-1 text-[10px] font-normal text-[var(--text-muted)]">
              / {briefingItems.length} 件 × ¥{briefingFee.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="rounded-md border border-[var(--accent)] bg-[var(--accent-subtle)] p-3">
          <div className="text-[10px] text-[var(--accent)]">選択中の合計 (税抜)</div>
          <div className="text-lg font-semibold tabular-nums text-[var(--accent)]">
            ¥{grandTotal.toLocaleString()}
          </div>
        </div>
      </div>

      {/* セクション A: 商品報酬 */}
      {pendingRecords.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium">
            <Coins className="size-3.5" />
            商品報酬
            <span className="text-[10px] font-normal text-[var(--text-muted)]">
              入金確定済みの報酬レコード
            </span>
          </div>
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-[36px_2fr_1.2fr_100px_120px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              <button
                type="button"
                onClick={toggleAllRecords}
                title={allRecordsSelected ? 'すべて解除' : 'すべて選択'}
                className="text-[var(--accent)]"
              >
                {allRecordsSelected ? (
                  <CheckSquare className="size-3.5" />
                ) : (
                  <Square className="size-3.5" />
                )}
              </button>
              <div>商談 / 商品</div>
              <div>顧客</div>
              <div>入金日</div>
              <div className="text-right">報酬額</div>
            </div>
            {pendingRecords.map((r) => {
              const isSel = selectedRecords.has(r.path);
              return (
                <button
                  key={r.path}
                  type="button"
                  onClick={() => toggleRecord(r.path)}
                  className={`w-full grid grid-cols-[36px_2fr_1.2fr_100px_120px] gap-2 px-3 py-2 text-xs border-t border-[var(--border)] text-left transition-colors ${
                    isSel ? 'bg-[var(--accent-subtle)]' : 'hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  <div className="text-[var(--accent)] pt-0.5">
                    {isSel ? <CheckSquare className="size-3.5" /> : <Square className="size-3.5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{r.dealTitle}</div>
                    <div className="truncate text-[10px] text-[var(--text-muted)]">
                      {r.productName}
                    </div>
                  </div>
                  <div className="truncate">{r.contactName}</div>
                  <div className="tabular-nums text-[var(--text-muted)]">{fmtDate(r.paidAt)}</div>
                  <div className="text-right tabular-nums font-semibold">
                    ¥{r.finalAmount.toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* セクション B: 個別説明代行料 */}
      {briefingItems.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5 text-xs font-medium">
            <Presentation className="size-3.5" />
            個別説明代行料
            <span className="text-[10px] font-normal text-[var(--text-muted)]">
              未請求 {briefingItems.length} 件 × @¥{briefingFee.toLocaleString()} —
              入金の有無に関わらず請求できます。チェックを外した分は次回以降に持ち越し。
            </span>
          </div>
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-[36px_100px_2fr_1.2fr_120px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              <button
                type="button"
                onClick={toggleAllBriefings}
                title={allBriefingsSelected ? 'すべて解除' : 'すべて選択'}
                className="text-[var(--accent)]"
              >
                {allBriefingsSelected ? (
                  <CheckSquare className="size-3.5" />
                ) : (
                  <Square className="size-3.5" />
                )}
              </button>
              <div>実施日</div>
              <div>商談</div>
              <div>顧客</div>
              <div className="text-right">代行料</div>
            </div>
            {briefingItems.map((b) => {
              const key = briefingKey(b);
              const isSel = selectedBriefings.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleBriefing(key)}
                  className={`w-full grid grid-cols-[36px_100px_2fr_1.2fr_120px] gap-2 px-3 py-2 text-xs border-t border-[var(--border)] text-left transition-colors ${
                    isSel ? 'bg-[var(--accent-subtle)]' : 'hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  <div className="text-[var(--accent)] pt-0.5">
                    {isSel ? <CheckSquare className="size-3.5" /> : <Square className="size-3.5" />}
                  </div>
                  <div className="tabular-nums text-[var(--text-muted)]">
                    {fmtDate(b.conductedAt)}
                  </div>
                  <div className="truncate font-medium">{b.dealTitle}</div>
                  <div className="truncate">{b.contactName}</div>
                  <div className="text-right tabular-nums">
                    ¥{briefingFee.toLocaleString()}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* フッター: 内訳 + 発行 */}
      <div className="flex items-center gap-4 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
        <div className="flex-1 text-[11px] text-[var(--text-muted)]">
          報酬 {selectedRecordObjs.length} 件 ¥{recordTotal.toLocaleString()}
          {' ＋ '}個別説明代行 {selectedBriefingObjs.length} 件 ¥{briefingTotal.toLocaleString()}
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[var(--text-muted)]">請求合計 (税抜)</div>
          <div className="text-lg font-semibold tabular-nums">¥{grandTotal.toLocaleString()}</div>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setDialogOpen(true)}
          disabled={nothingSelected}
        >
          <FileText className="size-3.5" />
          内容を確認して発行
        </Button>
      </div>

      <SaInvoiceIssueDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        records={selectedRecordObjs}
        briefings={selectedBriefingObjs}
        briefingFee={briefingFee}
        onIssued={onIssued}
      />
    </div>
  );
}
